import type { ReactNode } from "react";
import type { AreaCode, ResolvedPlace, Store } from "../../shared/types";
import { AREA_META } from "../../shared/types";
import { usePlaceDetails } from "../hooks/usePlaceDetails";
import { PhotoGallery } from "./PhotoGallery";
import { RatingStars } from "./RatingStars";
import { SelectionBadges } from "./SelectionBadges";

function categoryLabel(category: string): string {
  const match = /^ramen_(.+)$/.exec(category);
  const code = match?.[1] as AreaCode | undefined;
  return code && AREA_META[code] ? AREA_META[code].label : category;
}

interface Props {
  store: Store;
  resolved: ResolvedPlace | null;
  onNavigate?: (storeId: string) => void;
}

export function StoreDetails({ store, resolved, onNavigate }: Props) {
  // hideGoogleInfo 指定の店舗では Place Details API を呼ばない。
  // enabled: !!placeId により、null を渡せばフェッチされない。
  const details = usePlaceDetails(
    store.hideGoogleInfo ? null : resolved?.placeId,
  );
  const data = details.data;

  return (
    <div className="flex flex-col gap-5 px-5 pb-8">
      {(() => {
        // 後継店舗カードの分岐:
        //  - status === "moved": 見出し「移転先」
        //  - status === "closed" + movedToName 設定: 見出し「関連店舗」
        //  リンク先:
        //  (a) movedToId あり → 内部 store への遷移ボタン
        //  (b) movedToId なし + movedToUrl あり → 外部リンク (tabelog でも公式サイトでも可)
        const {
          status,
          movedToId,
          movedToName,
          movedToArea,
          movedToAreaLabel,
          movedToUrl,
        } = store;
        if (
          status !== "moved" &&
          status !== "closed" &&
          status !== "renewed"
        ) {
          return null;
        }
        if (!movedToName) return null;
        const areaLabel =
          movedToAreaLabel ?? (movedToArea ? AREA_META[movedToArea].label : undefined);
        const heading =
          status === "closed"
            ? "関連店舗"
            : status === "renewed"
              ? "リニューアル先"
              : "移転先";
        if (movedToId) {
          return (
            <MovedToCard
              heading={heading}
              name={movedToName}
              areaLabel={areaLabel}
              onClick={() => onNavigate?.(movedToId)}
            />
          );
        }
        if (movedToUrl) {
          return (
            <MovedToCard
              heading={heading}
              name={movedToName}
              areaLabel={areaLabel}
              href={movedToUrl}
            />
          );
        }
        return null;
      })()}

      <Section title="選出歴">
        <div className="flex items-center gap-2">
          <SelectionBadges years={store.years} />
          <span className="text-xs text-slate-400">
            {store.selectionCount}回 /
            {store.firstSelectedYear === store.lastSelectedYear
              ? ` ${store.firstSelectedYear}`
              : ` ${store.firstSelectedYear}〜${store.lastSelectedYear}`}
          </span>
        </div>
      </Section>

      {store.manualAddress && (
        <Section title="住所">
          <p className="text-[13px] leading-relaxed text-slate-300">
            {store.manualAddress}
          </p>
        </Section>
      )}

      {!store.hideGoogleInfo && (
      <Section title="Google の情報">
        {!resolved && (
          <p className="text-xs text-slate-400">場所情報を解決中です...</p>
        )}
        {resolved && details.isLoading && (
          <p className="text-xs text-slate-400">読み込み中...</p>
        )}
        {details.isError && (
          <p className="text-xs text-rose-300">Google 情報の取得に失敗しました</p>
        )}
        {data && (
          <div className="space-y-3">
            {typeof data.rating === "number" && (
              <div className="flex items-center gap-2">
                <RatingStars rating={data.rating} />
                <span className="text-lg font-bold text-slate-50">
                  {data.rating.toFixed(1)}
                </span>
                {data.userRatingCount !== undefined && (
                  <span className="text-xs text-slate-400">
                    ({data.userRatingCount.toLocaleString()}件)
                  </span>
                )}
              </div>
            )}
            {data.address && (
              <p className="text-[13px] leading-relaxed text-slate-300">
                {data.address}
              </p>
            )}
            {data.openingHours && data.openingHours.weekdayDescriptions.length > 0 && (
              <details className="rounded-xl border border-white/10 bg-white/5 p-3">
                <summary className="cursor-pointer text-xs font-semibold text-slate-200">
                  営業時間
                </summary>
                <ul className="mt-3 space-y-1 text-xs text-slate-300">
                  {data.openingHours.weekdayDescriptions.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </details>
            )}
            <PhotoGallery photos={data.photos} />
            {data.fetchedAt && (
              <p className="text-[10px] text-slate-500">
                Google 情報取得日: {new Date(data.fetchedAt).toLocaleDateString("ja-JP")}
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {data.googleMapsUri && (
                <LinkPill href={data.googleMapsUri}>Google Maps で開く</LinkPill>
              )}
              {data.websiteUri && (
                <LinkPill href={data.websiteUri}>公式サイト</LinkPill>
              )}
              {data.internationalPhoneNumber && (
                <LinkPill href={`tel:${data.internationalPhoneNumber}`}>
                  電話
                </LinkPill>
              )}
            </div>
          </div>
        )}
      </Section>
      )}

      <Section title="食べログ">
        {(store.pref || store.neighborhood) && (
          <p className="mb-2 text-[11px] text-slate-400">
            {[store.pref, store.neighborhood].filter(Boolean).join(" / ")}
          </p>
        )}
        {store.tabelogUrl && (
          <div className="mb-3">
            <LinkPill href={store.tabelogUrl}>
              食べログ店舗ページを開く
            </LinkPill>
          </div>
        )}
        <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          百名店アワード (年度別)
        </h4>
        <ul className="flex flex-wrap gap-1.5">
          {store.tabelogAwardUrls.map((entry) => {
            const showCategory =
              new Set(store.tabelogAwardUrls.map((e) => e.category)).size > 1;
            return (
              <li key={`${entry.year}-${entry.category}`}>
                <LinkPill href={entry.url}>
                  {entry.year}
                  {showCategory && (
                    <span className="ml-1 rounded bg-white/10 px-1 text-[9px] font-normal">
                      {categoryLabel(entry.category)}
                    </span>
                  )}
                </LinkPill>
              </li>
            );
          })}
        </ul>
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-white/10 pt-4 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** 別タブで開くことを示す極小サイズの外向き矢印アイコン (バッジ内に置く用)。 */
function ExternalArrowMini() {
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

type MovedToCardProps = {
  heading: string;
  name: string;
  areaLabel: string | undefined;
} & (
  | { onClick: () => void; href?: undefined }
  | { href: string; onClick?: undefined }
);

function MovedToCard(props: MovedToCardProps) {
  const { heading, name, areaLabel } = props;
  const isExternal = props.href !== undefined;
  // ホスト判定。href は override JSON 由来で trusted なので単純な include 判定で十分。
  const isTabelog = isExternal && props.href.includes("tabelog.com");
  const sharedClass =
    "group flex items-center gap-3 rounded-2xl border border-teal-400/40 bg-gradient-to-br from-teal-500/15 to-teal-600/10 px-4 py-3 text-left shadow-lg shadow-black/30 transition hover:from-teal-500/25 hover:to-teal-600/20 hover:border-teal-300/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300/50";

  const inner = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-500/25 text-teal-100 ring-1 ring-inset ring-teal-300/40">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-teal-300">
            {heading}
          </span>
          {isTabelog ? (
            // 食べログリンクは brand orange のバッジで明示。外向き矢印で別タブを示唆。
            <span className="inline-flex items-center gap-0.5 rounded-sm bg-[#ff6c00] px-1 py-[1px] text-[9px] font-bold uppercase leading-none text-white shadow-sm shadow-black/30">
              食べログ
              <ExternalArrowMini />
            </span>
          ) : isExternal ? (
            // 食べログ以外の外部リンクは「別タブ」表示で別タブ遷移を明示。
            <span className="inline-flex items-center gap-0.5 rounded-sm border border-teal-300/50 bg-teal-500/25 px-1 py-[1px] text-[9px] font-bold leading-none text-teal-100">
              別タブ
              <ExternalArrowMini />
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 truncate text-[15px] font-bold text-slate-50">
          {name}
        </div>
        {areaLabel && (
          <div className="mt-0.5 text-[11px] text-teal-200/80">{areaLabel}</div>
        )}
      </div>
      <span className="shrink-0 text-teal-200 transition group-hover:translate-x-0.5">
        {isExternal ? (
          // 外部リンク用アイコン (新規タブで開くことを示唆)
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M7 17 17 7" />
            <path d="M8 7h9v9" />
          </svg>
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </span>
    </>
  );

  if (isExternal) {
    return (
      <a
        href={props.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${heading}の ${name} を新しいタブで開く`}
        className={sharedClass}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={`${heading}の ${name} へ移動`}
      className={sharedClass}
    >
      {inner}
    </button>
  );
}

function LinkPill({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-100 transition hover:bg-white/10"
    >
      {children}
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M7 17 17 7" />
        <path d="M8 7h9v9" />
      </svg>
    </a>
  );
}
